import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

interface Testimonial {
  name: string;
  day: string;
  quote: string;
  results: {
    weight?: string;
    measurements?: string;
  };
}

export default function TestimonialSlider() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const testimonials: Testimonial[] = [
    {
      name: "Doro K.",
      day: "Tag 4",
      quote: "Das Workout heute war mega. Und Siggi haut einfach immer den coolsten Spruch zur richtigen Zeit raus 😄. Auch wenn es richtig anstrengend ist und ich schwitze wie verrückt, macht es mir unglaublich Spaß. Ich platze fast vor Energie und guter Laune. Die vegane Ernährung läuft problemlos – und gestern gab's schon das erste Kompliment, dass ich so schön abgenommen hätte 😉.",
      results: {}
    },
    {
      name: "Lisa K.",
      day: "Tag 7",
      quote: "Ich bin heute bei Tag 7 angekommen 😅. Ich hätte nie gedacht, dass es so anstrengend ist, aber es macht einfach unfassbar viel Spaß. Dass heute ein Pausentag war, hat mich total überrascht – ich wollte eigentlich weitertrainieren 😅.",
      results: {}
    },
    {
      name: "Claudia G.",
      day: "Tag 7",
      quote: "7 Tage geschafft! 👏👏👏 Ich hatte Zweifel, ob ich durchhalte – aber es wurde jeden Tag besser. An Tag 4 hatte ich meinen Tiefpunkt, danach ging's steil bergauf. Das Fett schmilzt wirklich! 💦🔥",
      results: {
        weight: "–1,85 kg",
        measurements: "in 7 Tagen"
      }
    },
    {
      name: "Pia M.",
      day: "Tag 7",
      quote: "Und was soll ich sagen? Bämm! Ich fühle mich so fit wie schon lange nicht mehr 💪. Meine Knie knacken nicht mehr beim Treppensteigen. Danke Siggi Spaleck für dieses Wohlbefinden! 💪💪💪",
      results: {
        weight: "–1,6 kg",
        measurements: "–8 cm Umfang"
      }
    },
    {
      name: "Svetlana",
      day: "Tag 8",
      quote: "Heute ist Tag 8 des 7-Tage-Body-Boost und ich bin einfach nur sprachlos. Ich bin so happy und fühle mich großartig. Siggi Spaleck – danke für dieses super Programm! Ich weiß nicht, wie oft ich dich in den Videos verflucht habe 😄… aber es hat sich gelohnt!",
      results: {
        weight: "–2,7 kg",
        measurements: "–9,5 cm Umfang"
      }
    },
    {
      name: "Max",
      day: "Tag 8",
      quote: "Ich hab's geschafft und bin total überwältigt! Der Anfang war hart, aber es hat sich so gelohnt! Ich war nach wenigen Tagen viel fitter, klarer und voll motiviert 😄. Ich bin stolz, drangeblieben zu sein. Danke Siggi – ihr habt mein Jahr verändert!",
      results: {
        weight: "–2,4 kg",
        measurements: "–11,5 cm (davon –5,5 cm am Bauch)"
      }
    },
    {
      name: "Hanna",
      day: "Tag 8",
      quote: "Ich bin so stolz, dass ich durchgezogen habe! Ich hatte Angst vor der Ernährung, aber ich hab durchgehalten 😄💪. Auf den Bildern sieht man richtig, was sich getan hat! Danke Siggi Spaleck für das tolle Programm. Ich mache es definitiv nochmal!",
      results: {
        weight: "–1,05 kg",
        measurements: "–6,5 cm Umfang"
      }
    },
    {
      name: "Janina E.",
      day: "Tag 8",
      quote: "Ich habe den 7-Tage-Body-Boost erfolgreich abgeschlossen! Ich sehe frischer aus, die Beine sind schlanker und ich bekomme viele Komplimente. Der grüne Smoothie bleibt bei mir – meine Kolleginnen wollen ihn sogar mittrinken 😄. Danke Siggi für den Input und das starke Programm!",
      results: {
        weight: "–1,05 kg",
        measurements: "–3,75 cm Bauch, –2,5 cm Po"
      }
    },
    {
      name: "Maike",
      day: "Tag 8",
      quote: "Heute ist Tag 8 – und ich wollte unbedingt Danke sagen 😊. Ich habe trotz einiger harter Momente durchgezogen und bin super happy! 🤩 Ich fühle mich deutlich leichter und beweglicher 🎉. Danke für Motivation, Struktur und Unterstützung!",
      results: {
        weight: "–1,5 kg",
        measurements: "–14 cm Umfang"
      }
    }
  ];

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      const newScrollLeft = direction === 'left'
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScrollButtons();
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);

      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, []);

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Das sagen unsere Kunden über den 7-Tage-Body-Boost
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Echte Ergebnisse von echten Menschen in nur 7–14 Tagen
          </p>
        </div>

        <div className="relative">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/95 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 hidden lg:block"
              aria-label="Vorheriges Testimonial"
            >
              <ChevronLeft className="w-6 h-6 text-gray-900" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4 cursor-grab active:cursor-grabbing"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[85%] sm:w-[70%] md:w-[45%] lg:w-[30%] snap-start"
              >
                <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-6 lg:p-8 h-full border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-xl text-gray-900">{testimonial.name}</h3>
                      <span className="inline-block mt-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                        {testimonial.day}
                      </span>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>

                  {(testimonial.results.weight || testimonial.results.measurements) && (
                    <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border-l-4 border-green-500">
                      {testimonial.results.weight && (
                        <div className="font-bold text-2xl text-green-700 mb-1">
                          {testimonial.results.weight}
                        </div>
                      )}
                      {testimonial.results.measurements && (
                        <div className="text-green-600 font-semibold">
                          {testimonial.results.measurements}
                        </div>
                      )}
                    </div>
                  )}

                  <blockquote className="text-gray-700 leading-relaxed italic">
                    "{testimonial.quote}"
                  </blockquote>
                </div>
              </div>
            ))}
          </div>

          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/95 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-300 hover:scale-110 hidden lg:block"
              aria-label="Nächstes Testimonial"
            >
              <ChevronRight className="w-6 h-6 text-gray-900" />
            </button>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 mt-8">
          <div className="flex gap-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (scrollContainerRef.current) {
                    const cardWidth = scrollContainerRef.current.clientWidth * 0.85;
                    scrollContainerRef.current.scrollTo({
                      left: index * cardWidth,
                      behavior: 'smooth'
                    });
                  }
                }}
                className="w-2 h-2 rounded-full bg-gray-300 hover:bg-green-500 transition-colors"
                aria-label={`Gehe zu Testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            ✓ Verifizierte Kundenerfahrungen
          </p>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}
